import { useDispatch, useSelector } from 'react-redux'
import SettingsNavMenu from './SettingsNavMenu'
import { checkIfOrgHasDemoData, selectSettings, setCurrentPage } from '../../reducers/settings'
import AdminSettings from './Admin'
import { useNavigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import SsoSettings from './SSO'
import MdmSettings from './MDM'
import NotificationSettings from './Notifications'
import { selectUser, setDarkMode, setSession } from '../../reducers/users'
import { useEffect } from 'react'
import AuditLogs from './AuditLogs'
import WebhookSettings from './Webhooks'
import ApiSettings from './API'
import { getRoles, permitsRole } from '../../utils/roles'
import { RoleType } from '../../types/UserTypes'
import DemoDataSettings from './DemoData'
import { useAppDispatch } from '../../reducers/store'
import SecureDns from './SecureDns'

function SettingsWrapper() {
  const { currentPage } = useSelector(selectSettings)
  const { session, darkModeEnabled } = useSelector(selectUser)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const di = useAppDispatch()
  const storedSettingsPage = localStorage.getItem('SETTINGS_PAGE_COOKIE')
  const goToConfigured = localStorage.getItem('SSO_CONFIGURED_COOKIE')
  const roles = getRoles(session)
  const rolePermitsAdmins = permitsRole(roles, [RoleType.SUPER_ADMIN])
  const rolePermitsPlatform = permitsRole(roles, [RoleType.PLATFORM_MGMT])

  useEffect(() => {
    di(checkIfOrgHasDemoData())
    if (goToConfigured) {
      dispatch(setCurrentPage('SSO'))
    } else if (storedSettingsPage) {
      dispatch(setCurrentPage(storedSettingsPage))
    } else {
      const defaultPage = rolePermitsAdmins ? 'ADMIN' : rolePermitsPlatform ? 'SSO' : 'CONDITIONAL_ACCESS'
      localStorage.setItem('SETTINGS_PAGE_COOKIE', currentPage ? currentPage : defaultPage)
    }
  }, [])

  const selectMainContent = () => {
    switch (currentPage) {
      case 'ADMIN':
        return <AdminSettings />
      case 'AUDIT':
        return <AuditLogs />
      case 'SSO':
        return <SsoSettings />
      case 'WEBHOOKS':
        return <WebhookSettings />
      case 'API':
        return <ApiSettings />
      case 'MDM':
        return <MdmSettings />
      case 'NOTIFICATION':
        return <NotificationSettings />
      case 'CUSTOM_DNS':
        return <SecureDns />
      case 'DEMO_DATA':
        return <DemoDataSettings />
      default:
        return null
    }
  }

  const selectTitle = () => {
    switch (currentPage) {
      case 'ADMIN':
        return 'Administrators'
      case 'AUDIT':
        return 'Audit Logs'
      case 'SSO':
        return 'Single Sign On Provider'
      case 'WEBHOOKS':
        return 'Webhooks'
      case 'API':
        return 'API Access'
      case 'MDM':
        return 'MDM Configuration'
      case 'NOTIFICATION':
        return null
      case 'CUSTOM_DNS':
        return null
      case 'DEMO_DATA':
        return 'Demo Data'
      default:
        return 'Administrators'
    }
  }

  useEffect(() => {
    const localSession = localStorage.getItem('session') as string
    if (!session && localSession === '{}') {
      localStorage.setItem('session', '{}')
      localStorage.removeItem('SSO_CONFIGURED_COOKIE')
      dispatch(setSession(undefined))
      navigate('/login')
    }
  }, [session])

  const getCookie = (name: string) => {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
    return match ? match[2] : undefined
  }

  useEffect(() => {
    const savedDarkMode = getCookie('DARK_MODE')
    if (savedDarkMode !== undefined) {
      dispatch(setDarkMode(savedDarkMode === 'true' ? true : false))
    }
  }, [])

  useEffect(() => {
    if (darkModeEnabled) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }, [darkModeEnabled])

  return (
    <>
      <div className="w-full h-screen mx-auto flex gap-10 light dark:dark bg-paginationBackground">
        {/* Nav Menu */}
        <SettingsNavMenu />
        <div className="flex flex-col gap-4 flex-grow mt-8">
          <div className="container mx-auto">
            <div className="max-w-[1040px] mx-auto">
              <div className="flex">
                <p className="font-semibold text-xl flex-grow">{selectTitle()}</p>
              </div>
              {/* Main content */}
              <div className="pr-8">{selectMainContent()}</div>
            </div>
          </div>
        </div>
      </div>
      <Toaster
        toastOptions={{
          style: {
            maxWidth: 500,
          },
        }}
      />
    </>
  )
}

export default SettingsWrapper
